import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { RequestFeedbackModal } from '../components/RequestFeedbackModal';
import {
  BellRing,
  ChevronRight,
  Eye,
  MessageCirclePlus,
  RefreshCw,
  X,
  AlertCircle,
  Inbox as InboxIcon,
  MessageSquare,
  ChevronDown,
} from 'lucide-react';
import { useApiClient } from '../hooks/useApiClient';
import { useUserProfileContext } from '../context/UserProfileContext';
import { useToast } from '../hooks/useToast';
import { Toast } from '../components/Toast';
import { Pagination } from '../components/common/Pagination';
import {
  getFeedbackReceived,
  getFeedbackRequested,
  getFeedbackProvided,
  getPendingActions,
  sendReminder,
  PeerFeedbackFilters,
} from '../utils/peerFeedbackApi';
import { PeerFeedbackSummary } from '../types/peerFeedback';
import { formatDateInIST } from '../utils/mappers';

// ── Types ────────────────────────────────────────────────────────────────────

type Tab = 'inbox' | 'sent' | 'given';

const TABS: { id: Tab; label: string }[] = [
  { id: 'inbox', label: 'Inbox' },
  { id: 'sent',  label: 'Requested by me' },
  { id: 'given', label: 'Given by me' },
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function StatusPill({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending:   'bg-yellow-50 text-yellow-700 border-yellow-200',
    responded: 'bg-green-50  text-green-700  border-green-200',
    declined:  'bg-red-50    text-red-700    border-red-200',
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide border whitespace-nowrap ${
        styles[status] ?? 'bg-slate-100 text-slate-600 border-slate-200'
      }`}
    >
      {status}
    </span>
  );
}

function Avatar({ name, accent }: { name: string; accent: 'pink' | 'amber' | 'indigo' | 'emerald' }) {
  const initials = name
    .split(' ')
    .map(p => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const palette: Record<string, string> = {
    pink:    'bg-pink-100 text-pink-700',
    amber:   'bg-amber-100 text-amber-700',
    indigo:  'bg-indigo-100 text-indigo-700',
    emerald: 'bg-emerald-100 text-emerald-700',
  };
  return (
    <div
      className={`flex items-center justify-center w-11 h-11 rounded-full font-bold text-sm flex-shrink-0 ${palette[accent]}`}
    >
      {initials || '?'}
    </div>
  );
}

function formatDate(s: string) {
  return formatDateInIST(s);
}

// Single rich card used in the priority sections of the Inbox tab.
interface PriorityCardProps {
  item: PeerFeedbackSummary;
  variant: 'action' | 'received';
  onActivate: () => void;
  primaryLabel: string;
  PrimaryIcon: React.ComponentType<{ className?: string }>;
}

function PriorityCard({ item, variant, onActivate, primaryLabel, PrimaryIcon }: PriorityCardProps) {
  const isAction = variant === 'action';
  const counterpartName = isAction ? item.requesterName : item.recipientName;
  const counterpartEmail = isAction ? item.requesterEmail : item.recipientEmail;
  const accent = isAction ? 'pink' : 'amber';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onActivate}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onActivate(); } }}
      className={`bg-white rounded-2xl border-l-4 border border-slate-200 hover:bg-pink-50/40 hover:shadow-md hover:border-slate-300 cursor-pointer transition-all focus:outline-none focus:ring-2 focus:ring-pink-500/30 ${
        isAction ? 'border-l-pink-500' : 'border-l-amber-400'
      }`}
    >
      <div className="flex items-center gap-4 px-5 py-4">
        <Avatar name={counterpartName} accent={accent} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900 text-sm" title={counterpartEmail}>
              {counterpartName}
            </span>
            <StatusPill status={item.status} />
            <span className="text-xs text-slate-500">· {formatDate(item.createdAt)}</span>
          </div>
          {item.message && (
            <p className="text-sm text-slate-600 mt-1 italic truncate">
              "{item.message}"
            </p>
          )}
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onActivate(); }}
          className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-xl transition-all flex-shrink-0 ${
            isAction
              ? 'bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white shadow-sm shadow-pink-500/20'
              : 'bg-pink-50 hover:bg-pink-100 text-pink-600 border border-pink-200'
          }`}
        >
          <PrimaryIcon className="w-4 h-4" />
          {primaryLabel}
        </button>
      </div>
    </div>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

export function PeerFeedbackPage() {
  const { apiCall } = useApiClient();
  const { userProfile } = useUserProfileContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { toasts, hideToast, info: showInfo } = useToast();

  // URL-driven state (tab + year + month). Initialise once from URL.
  const initialFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab') as Tab | null;
    // Back-compat: old `chip` param
    const chipParam = params.get('chip');
    const tabFromChip: Tab | null =
      chipParam === 'sent' ? 'sent' : chipParam === 'given' ? 'given' : null;
    return {
      tab: (tabParam && TABS.some(t => t.id === tabParam) ? tabParam : tabFromChip) ?? ('inbox' as Tab),
      year:  params.get('year')  ? Number(params.get('year'))  : undefined,
      month: params.get('month') ? Number(params.get('month')) : undefined,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [tab, setTab] = useState<Tab>(initialFromUrl.tab);
  const [year, setYear] = useState<number | undefined>(initialFromUrl.year);
  const [month, setMonth] = useState<number | undefined>(initialFromUrl.month);

  // Buckets — fetched once per (year, month). Each tab is a derived view.
  const [pending, setPending]     = useState<PeerFeedbackSummary[]>([]);
  const [received, setReceived]   = useState<PeerFeedbackSummary[]>([]);
  const [requested, setRequested] = useState<PeerFeedbackSummary[]>([]);
  const [provided, setProvided]   = useState<PeerFeedbackSummary[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remindingId, setRemindingId] = useState<number | null>(null);
  const [showRequestModal, setShowRequestModal] = useState(false);

  // Sync URL when state changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (tab !== 'inbox') params.set('tab', tab);
    if (year != null)    params.set('year', String(year));
    if (month != null)   params.set('month', String(month));
    const qs = params.toString();
    const next = qs ? `?${qs}` : '';
    if (next !== location.search) {
      navigate({ pathname: location.pathname, search: next }, { replace: true });
    }
  }, [tab, year, month, location.pathname, location.search, navigate]);

  const fetchAll = useCallback(async () => {
    if (!userProfile) return;
    setLoading(true);
    setError(null);
    try {
      const id = userProfile.employeeId;
      const filters: PeerFeedbackFilters = { year, month };
      const [p, r, rec, prov] = await Promise.all([
        getPendingActions(id, apiCall, filters),
        getFeedbackRequested(id, apiCall, filters),
        getFeedbackReceived(id, apiCall, filters),
        getFeedbackProvided(id, apiCall, filters),
      ]);
      setPending(p);
      setRequested(r);
      setReceived(rec);
      setProvided(prov);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [userProfile, apiCall, year, month]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleRemind = async (item: PeerFeedbackSummary) => {
    if (!userProfile) return;
    setRemindingId(item.id);
    try {
      await sendReminder(item.id, userProfile.employeeId, apiCall);
      fetchAll();
    } catch (err: any) {
      alert(err?.message ?? 'Could not send reminder');
    } finally {
      setRemindingId(null);
    }
  };

  // Only show open/pending requests in "Requested by me".
  const pendingRequested = useMemo(
    () => requested.filter(r => r.status === 'pending'),
    [requested]
  );

  // Emails of recipients with an open (pending) request — used to block re-requests.
  const pendingRecipientEmails = useMemo(() => {
    const set = new Set<string>();
    for (const r of pendingRequested) {
      if (r.recipientEmail) set.add(r.recipientEmail.toLowerCase());
    }
    return set;
  }, [pendingRequested]);

  const counts = useMemo(() => ({
    inbox: pending.length + received.length,
    sent:  pendingRequested.length,
    given: provided.length,
  }), [pending, received, pendingRequested, provided]);

  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    return [current, current - 1, current - 2, current - 3, current - 4];
  }, []);

  const filtersActive = year != null || month != null;

  return (
    <div className="min-h-screen bg-slate-50 pt-20">
      <main className="mx-auto px-6 lg:px-8 py-6 max-w-screen-2xl">
        {/* Hero header — matches Connect home page */}
        <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 rounded-2xl px-8 py-7 mb-6 shadow-lg text-white flex items-center justify-between gap-6 flex-wrap">
          <div className="flex items-center gap-5 min-w-0">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-white/15 backdrop-blur-sm flex-shrink-0">
              <MessageSquare className="w-8 h-8 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold leading-tight">Peer Feedback</h1>
              <p className="text-white/80 text-sm mt-1">Manage requests you've sent and received.</p>
            </div>
          </div>
          <button
            onClick={() => setShowRequestModal(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/15 hover:bg-white border border-white/40 text-white hover:text-pink-600 text-sm font-semibold transition-all flex-shrink-0 shadow-sm backdrop-blur-sm"
          >
            <MessageCirclePlus className="w-4 h-4" />
            New Request
          </button>
        </div>

        {showRequestModal && (
          <RequestFeedbackModal
            onClose={() => setShowRequestModal(false)}
            onSuccess={() => { setShowRequestModal(false); fetchAll(); }}
            pendingRecipientEmails={pendingRecipientEmails}
            onDuplicateRecipient={(name) =>
              showInfo(`You already requested feedback from ${name}. Go to "Requested by me" tab and remind them.`)
            }
          />
        )}

        {/* Toasts */}
        {toasts.map(t => (
          <Toast key={t.id} message={t.message} type={t.type} onClose={() => hideToast(t.id)} />
        ))}

        {/* Tabs + filters in a card to match the Connect FilterBar */}
        <div className="bg-white rounded-2xl p-4 mb-6 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-1 flex-wrap">
              {TABS.map(t => {
                const isActive = tab === t.id;
                const count = counts[t.id];
                return (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl transition-all ${
                      isActive
                        ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-sm shadow-pink-500/20'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                    }`}
                  >
                    <span>{t.label}</span>
                    {count > 0 && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                        isActive ? 'bg-white/25 text-white' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-3">
              <div className="relative inline-flex items-center">
                <select
                  value={year ?? ''}
                  onChange={e => setYear(e.target.value ? Number(e.target.value) : undefined)}
                  className="bg-slate-50 text-slate-800 border border-slate-200 rounded-xl pl-4 pr-9 py-2.5 text-sm font-medium appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-300 transition-all"
                >
                  <option value="">All Years</option>
                  {yearOptions.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
              <div className="relative inline-flex items-center">
                <select
                  value={month ?? ''}
                  onChange={e => setMonth(e.target.value ? Number(e.target.value) : undefined)}
                  disabled={year == null}
                  title={year == null ? 'Select a year first' : ''}
                  className="bg-slate-50 text-slate-800 border border-slate-200 rounded-xl pl-4 pr-9 py-2.5 text-sm font-medium appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-300 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <option value="">All Months</option>
                  {MONTHS.map((m, i) => (
                    <option key={m} value={i + 1}>{m}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
              {filtersActive && (
                <button
                  onClick={() => { setYear(undefined); setMonth(undefined); }}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-medium text-slate-500 hover:text-pink-600 hover:bg-pink-50 transition-colors"
                  title="Clear filters"
                >
                  <X className="w-4 h-4" />
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400 gap-2">
            <RefreshCw className="w-5 h-5 animate-spin text-pink-500" />
            <span className="text-slate-600 text-sm font-medium">Loading…</span>
          </div>
        ) : error ? (
          <div className="bg-white rounded-2xl shadow-sm border border-red-200 text-center py-10 text-red-600 text-sm">{error}</div>
        ) : tab === 'inbox' ? (
          <InboxView
            pending={pending}
            received={received}
            onProvide={(it) => navigate(`/feedback/provide/${it.id}`)}
            onView={(it) => navigate(`/feedback/view/${it.id}`)}
          />
        ) : tab === 'sent' ? (
          <SentList
            items={pendingRequested}
            remindingId={remindingId}
            onRemind={handleRemind}
            onView={(it) => navigate(`/feedback/view/${it.id}`)}
          />
        ) : (
          <GivenList
            items={provided}
            onView={(it) => navigate(`/feedback/view/${it.id}`)}
          />
        )}
      </main>
    </div>
  );
}

// ── Sub-views ────────────────────────────────────────────────────────────────

function SectionHeader({
  icon, title, count, accent,
}: {
  icon: React.ReactNode;
  title: string;
  count: number;
  accent: 'pink' | 'amber';
}) {
  const palette = accent === 'pink'
    ? 'text-pink-600 bg-pink-50 border-pink-200'
    : 'text-amber-600 bg-amber-50 border-amber-200';
  return (
    <div className="flex items-center gap-2 mb-3">
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${palette}`}>
        {icon}
        {title}
      </span>
      <span className="text-xs font-medium text-slate-500">{count} item{count === 1 ? '' : 's'}</span>
    </div>
  );
}

function InboxView({
  pending, received, onProvide, onView,
}: {
  pending: PeerFeedbackSummary[];
  received: PeerFeedbackSummary[];
  onProvide: (i: PeerFeedbackSummary) => void;
  onView:    (i: PeerFeedbackSummary) => void;
}) {
  const showEmpty = pending.length === 0 && received.length === 0;
  if (showEmpty) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 text-center py-16 text-slate-400">
        <InboxIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
        <p className="text-base font-semibold text-slate-600">You're all caught up</p>
        <p className="text-sm mt-1">No pending requests or new responses right now.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {pending.length > 0 && (
        <section>
          <SectionHeader
            icon={<AlertCircle className="w-3.5 h-3.5" />}
            title="Action required"
            count={pending.length}
            accent="pink"
          />
          <div className="space-y-3">
            {pending.map(item => (
              <PriorityCard
                key={item.id}
                item={item}
                variant="action"
                primaryLabel="Provide"
                PrimaryIcon={ChevronRight}
                onActivate={() => onProvide(item)}
              />
            ))}
          </div>
        </section>
      )}

      <section>
        <SectionHeader
          icon={<InboxIcon className="w-3.5 h-3.5" />}
          title="Feedback received"
          count={received.length}
          accent="amber"
        />
        {received.length === 0 ? (
          <div className="text-center py-10 text-slate-400 bg-white rounded-2xl border border-dashed border-slate-200">
            <p className="text-sm">No responses yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {received.map(item => (
              <PriorityCard
                key={item.id}
                item={item}
                variant="received"
                primaryLabel="View"
                PrimaryIcon={Eye}
                onActivate={() => onView(item)}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

interface RowProps {
  item: PeerFeedbackSummary;
  name: string;
  email: string;
  accent: 'indigo' | 'emerald';
  onActivate: () => void;
  action: React.ReactNode;
  isLast: boolean;
}

function ListRow({ item, name, email, accent, onActivate, action, isLast }: RowProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onActivate}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onActivate(); } }}
      className={`flex items-center gap-4 px-5 py-4 hover:bg-pink-50/40 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-pink-500/30 ${
        isLast ? '' : 'border-b border-slate-100'
      }`}
    >
      <Avatar name={name} accent={accent} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-slate-900 text-sm" title={email}>
            {name}
          </span>
          <StatusPill status={item.status} />
          <span className="text-xs text-slate-500">· {formatDate(item.createdAt)}</span>
        </div>
        {item.message && (
          <p className="text-sm text-slate-600 mt-1 italic truncate">"{item.message}"</p>
        )}
      </div>
      <div className="flex-shrink-0">{action}</div>
    </div>
  );
}

function SentList({
  items, remindingId, onRemind, onView,
}: {
  items: PeerFeedbackSummary[];
  remindingId: number | null;
  onRemind: (i: PeerFeedbackSummary) => void;
  onView:   (i: PeerFeedbackSummary) => void;
}) {
  const [page, setPage] = useState(1);
  const pageSize = 20;
  React.useEffect(() => { setPage(1); }, [items.length]);
  if (items.length === 0) return <EmptyState text="No open requests. All your peer feedback requests have been answered." />;
  const totalPages = Math.ceil(items.length / pageSize);
  const start = (page - 1) * pageSize;
  const visible = items.slice(start, start + pageSize);
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
      {visible.map((item, idx) => {
        const isPending = item.status === 'pending';
        const onActivate = isPending
          ? () => { if (item.canRemind && remindingId !== item.id) onRemind(item); }
          : () => onView(item);
        const action = isPending ? (
          <button
            onClick={(e) => { e.stopPropagation(); onRemind(item); }}
            disabled={!item.canRemind || remindingId === item.id}
            title={!item.canRemind ? 'Wait 3 days between reminders' : 'Send reminder'}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-pink-50 hover:bg-pink-100 disabled:opacity-40 disabled:cursor-not-allowed text-pink-600 border border-pink-200 rounded-xl transition-colors"
          >
            <BellRing className="w-4 h-4" />
            {remindingId === item.id ? 'Sending…' : 'Remind'}
          </button>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); onView(item); }}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-xl transition-all shadow-sm shadow-pink-500/20"
          >
            <Eye className="w-4 h-4" />
            View
          </button>
        );
        return (
          <ListRow
            key={item.id}
            item={item}
            name={item.recipientName}
            email={item.recipientEmail}
            accent="indigo"
            onActivate={onActivate}
            action={action}
            isLast={idx === visible.length - 1}
          />
        );
      })}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalItems={items.length}
        itemsPerPage={pageSize}
      />
    </div>
  );
}

function GivenList({
  items, onView,
}: {
  items: PeerFeedbackSummary[];
  onView: (i: PeerFeedbackSummary) => void;
}) {
  const [page, setPage] = useState(1);
  const pageSize = 20;
  React.useEffect(() => { setPage(1); }, [items.length]);
  if (items.length === 0) return <EmptyState text="You haven't provided feedback to anyone in this period." />;
  const totalPages = Math.ceil(items.length / pageSize);
  const start = (page - 1) * pageSize;
  const visible = items.slice(start, start + pageSize);
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
      {visible.map((item, idx) => (
        <ListRow
          key={item.id}
          item={item}
          name={item.requesterName}
          email={item.requesterEmail}
          accent="emerald"
          onActivate={() => onView(item)}
          action={
            <button
              onClick={(e) => { e.stopPropagation(); onView(item); }}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-xl transition-all shadow-sm shadow-pink-500/20"
            >
              <Eye className="w-4 h-4" />
              View
            </button>
          }
          isLast={idx === visible.length - 1}
        />
      ))}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalItems={items.length}
        itemsPerPage={pageSize}
      />
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 text-center py-16 text-slate-400">
      <InboxIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
      <p className="text-sm font-medium text-slate-500">{text}</p>
    </div>
  );
}
